<template>
  <div class="BetterAssurance">
    <h2 class="gl_form-title small">
      {{ t('hiring.common.better-assurance.form-legend') }}
    </h2>

    <p class="hiring-subtitle">
      {{ t('hiring.common.better-assurance.subtitle') }}
    </p>

    <RadioButtonGroup
      id="better-assurance"
      :value="betterAssurance"
      v-model="betterAssurance"
      :options="betterAssuranceOptions"
      flexDirection="row"
      :rules="[validationRules.radioRequired]"
    >
    </RadioButtonGroup>

    <div class="actions">
      <BaseButton
        :ariaDisabled="!isValidForm"
        @click="submitAndContinue"
        :name="gtmPrefix + NEXT_BTN"
        :id="gtmPrefix + NEXT_BTN"
        :emitGTMEvent="{ event: GTMEventType.NEXT }"
      >
        {{ t('rater.base.next') }}
      </BaseButton>
    </div>

    <ConfirmRemoveCoveragesModal
      v-model:open="showAdviseModal"
      @close="showAdviseModal = false"
      @repricing="saveRepricing"
    />
  </div>
</template>

<script lang="ts" setup>
  import BaseButton from '@/modules/core/components/ui/BaseButton.vue'
  import RadioButtonGroup from '@/modules/core/components/ui/RadioButtonGroupComponent.vue'
  import { useRules } from '@/modules/core/composables/useRulesComposable'
  import type { OptionsValuesInterface } from '@/modules/core/models/types'
  import { useLoadingStore } from '@/modules/core/stores/loadingStore'
  import { booleanToString, stringToBoolean, stringToNumber } from '@/utils/converterUtils'
  import { inject, onBeforeMount, ref } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useForeignHealthStore } from '../../stores'
  import ConfirmRemoveCoveragesModal from '../betterAssurance/ConfirmRemoveCoveragesModal.vue'
  // import HealthHiringAnswers from '@/modules/health/hiring/models/APP/HealthHiringAnswers'
  // import type HealthHiringInsured from '@/modules/health/hiring/models/APP/HealthHiringInsured'
  // import HealthRaterAnswers from '@/modules/health/rater/models/APP/HealthRaterAnswers'
  import AdditionalCoverage from '@/modules/common/common/models/APP/AdditionalCoverage'
  import GTMEventType from '@/modules/core/models/GTMEventType'
  import { NEXT_BTN } from '@/modules/core/models/IdHTMLItems'
  import ForeignHealthRaterAnswers from '@/modules/foreign-health/rater/models/APP/ForeignHealthRaterAnswers'
  import ForeignHealthHiringAnswers from '../../models/APP/ForeignHealthHiringAnswers'
  import type ForeignHealthHiringInsured from '../../models/APP/ForeignHealthHiringInsured'

  const { t } = useI18n()
  const { required } = useRules()
  const foreignHealthStore = useForeignHealthStore()
  const loadingStore = useLoadingStore()
  defineExpose({ triggerSubmit })

  interface PropsInterface {
    nextForm: String[]
    answers: Record<string, any>
    configurations: Record<string, any>
    isValidForm: boolean
  }
  const props = withDefaults(defineProps<PropsInterface>(), {})
  const emit = defineEmits(['next', 'submit-coverages'])

  const validationRules = {
    radioRequired: (value: string) => required(value, 'common.validations.option')
  }

  const validateForm = inject<() => Promise<boolean>>('validateForm') ?? (async () => true)

  const betterAssuranceOptions: OptionsValuesInterface[] = [
    { label: t('common.choices.yes'), value: '1' },
    { label: t('common.choices.no'), value: '0' }
  ]

  const firstBetterAssuranceValue =
    props.answers.betterAssurance !== null ? booleanToString(props.answers.betterAssurance) : null
  const betterAssurance = ref(firstBetterAssuranceValue)
  const repricingAnswers = ref<ForeignHealthHiringAnswers | null>(null)
  const showAdviseModal = ref(false)

  const gtmPrefix = 'hiring_health_better_assurance_form_'

  async function submitAndContinue() {
    const isValid = await validateForm()
    if (!isValid) return

    const betterAssuranceValue = stringToBoolean(betterAssurance.value as string)
    const hiringStoredAnswers = foreignHealthStore.getHiringFormAnswers()
    repricingAnswers.value = new ForeignHealthHiringAnswers(hiringStoredAnswers)
    repricingAnswers.value.updateAttributes({ betterAssurance: betterAssuranceValue })
    const hasCoverages = hiringStoredAnswers.insureds.some((i) => i.additionalCoverages.length > 0)

    if (!betterAssuranceValue && hasCoverages) {
      await repricing()
      return
    }

    emit('next')
  }

  async function repricing() {
    const insureds = props.answers.insureds
    insureds.forEach((insured: ForeignHealthHiringInsured) => {
      insured.updateAttributes({ additionalCoverages: [] })
      if (props.answers.coverageCode) {
        const coverageCode = props.answers.coverageCode
        insured.additionalCoverages.push(
          new AdditionalCoverage({
            key: coverageCode,
            checked: true,
            value: null
          })
        )
      }
    })
    repricingAnswers.value!.updateAttributes({ insureds })

    showAdviseModal.value = true
  }

  async function saveRepricing() {
    const newAnswers = new ForeignHealthRaterAnswers(
      repricingAnswers.value as ForeignHealthHiringAnswers
    )
    const product = await foreignHealthStore.modifyRate(newAnswers)
    if (!product) throw new Error('Hiring product failed')

    emit('submit-coverages')
  }

  async function triggerSubmit() {
    const nextPageIndex = stringToNumber(betterAssurance.value as string)
    const answers = {
      betterAssurance: betterAssurance.value
    }
    const page = props.nextForm[nextPageIndex!]

    return { page, answers }
  }

  function beforeMount() {
    setTimeout(loadingStore.stopLoading, 300)
  }

  onBeforeMount(beforeMount)
</script>

<style lang="scss" scoped>
  .BetterAssurance {
    .hiring-subtitle {
      margin-bottom: $spacing-xl;
      color: $color-neutral;
      font-size: $font-size-l;
      font-weight: $font-weight-400;
    }
  }
</style>
